import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { portfolioData } from '../mock';
import { Filter, ArrowUpRight } from 'lucide-react';

const Work = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [hoveredProject, setHoveredProject] = useState(null);

  const categories = ['All', ...new Set(portfolioData.projects.map(p => p.category))];

  const filteredProjects = selectedCategory === 'All'
    ? portfolioData.projects
    : portfolioData.projects.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-b from-black to-zinc-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            Selected <span className="text-amber-400">Work</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            A showcase of visual communication projects spanning multiple mediums and disciplines, 
            each representing the continuous evolution of creative expression.
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 bg-zinc-900 sticky top-20 z-40 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-4 overflow-x-auto pb-2">
            <div className="flex items-center gap-2 text-gray-400 flex-shrink-0">
              <Filter size={20} />
              <span className="text-sm font-semibold">Filter:</span>
            </div>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-amber-400 text-black'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid - Compact Size */}
      <section className="py-16 bg-gradient-to-b from-zinc-900 to-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredProjects.map((project) => {
              return (
                <Link
                  key={project.id}
                  to={`/work/${project.id}`}
                  className="group relative overflow-hidden rounded-xl bg-zinc-900 border border-white/5 hover:border-amber-400/30 transition-all duration-500 cursor-pointer"
                  onMouseEnter={() => setHoveredProject(project.id)}
                  onMouseLeave={() => setHoveredProject(null)}
                >
                  {/* Image */}
                  <div className="relative overflow-hidden aspect-[4/3]">
                    <img
                      src={project.image}
                      alt={project.title}
                      crossOrigin="anonymous"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-500" />
                  </div>

                  {/* Content */}
                  <div className="absolute inset-x-0 bottom-0 p-4 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-amber-400 text-[10px] font-bold uppercase tracking-wider mb-1">
                          {project.category}
                        </p>
                        <h3 className="text-white text-sm md:text-base font-bold mb-1 line-clamp-2">
                          {project.title}
                        </h3>
                        <p className="text-gray-400 text-xs leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100 line-clamp-2">
                          {project.description}
                        </p>
                      </div>
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-400/10 border border-amber-400/20 flex items-center justify-center text-amber-400 opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:rotate-45">
                        <ArrowUpRight size={16} />
                      </div>
                    </div>
                  </div>

                  {/* Hover indicator */}
                  {hoveredProject === project.id && (
                    <div className="absolute top-2 right-2 px-2 py-1 bg-amber-400 text-black text-[10px] font-bold rounded-full">
                      VIEW
                    </div>
                  )}
                </Link>
              );
            })}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-24">
              <p className="text-gray-400 text-lg">No projects found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-black">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-sm border border-white/10 rounded-3xl p-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Like What You See?
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              Let's discuss how we can collaborate on your next project.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-amber-400 text-black font-semibold rounded-full hover:bg-amber-300 transition-all duration-300"
            >
              Get In Touch
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Work;